import { getNationality } from './onboardingStorage';

/** Major expat destinations — E.164 dial codes */
export const PHONE_DIAL_CODES = [
  { dial: '+82', labelKey: 'auth.forgot.dialKR', country: 'KR' },
  { dial: '+84', labelKey: 'auth.forgot.dialVN', country: 'VN' },
  { dial: '+81', labelKey: 'auth.forgot.dialJP', country: 'JP' },
  { dial: '+1', labelKey: 'auth.forgot.dialUSCA', country: 'US' },
  { dial: '+44', labelKey: 'auth.forgot.dialGB', country: 'GB' },
  { dial: '+61', labelKey: 'auth.forgot.dialAU', country: 'AU' },
  { dial: '+65', labelKey: 'auth.forgot.dialSG', country: 'SG' },
  { dial: '+60', labelKey: 'auth.forgot.dialMY', country: 'MY' },
  { dial: '+66', labelKey: 'auth.forgot.dialTH', country: 'TH' },
  { dial: '+63', labelKey: 'auth.forgot.dialPH', country: 'PH' },
  { dial: '+62', labelKey: 'auth.forgot.dialID', country: 'ID' },
  { dial: '+86', labelKey: 'auth.forgot.dialCN', country: 'CN' },
  { dial: '+7', labelKey: 'auth.forgot.dialRU', country: 'RU' },
  { dial: '+49', labelKey: 'auth.forgot.dialDE', country: 'DE' },
  { dial: '+33', labelKey: 'auth.forgot.dialFR', country: 'FR' },
];

const DIAL_DIGITS_DESC = [...PHONE_DIAL_CODES]
  .map(({ dial }) => String(dial).replace(/\D/g, ''))
  .sort((a, b) => b.length - a.length);

const NATIONALITY_TO_DIAL = Object.fromEntries(
  PHONE_DIAL_CODES.filter((r) => r.country).map((r) => [r.country, r.dial]),
);

/** @param {string | null | undefined} countryCode e.g. VN, KR */
export function defaultDialForCountry(countryCode) {
  const c = String(countryCode || '').trim().toUpperCase();
  if (c && NATIONALITY_TO_DIAL[c]) return NATIONALITY_TO_DIAL[c];
  try {
    const nat = String(getNationality() || '').trim().toUpperCase();
    if (NATIONALITY_TO_DIAL[nat]) return NATIONALITY_TO_DIAL[nat];
  } catch {
    /* ignore */
  }
  return '+84';
}

/**
 * Local number: digits only; strip a single leading 0 (KR/VN local format).
 * @param {string} national
 */
export function sanitizeNationalDigits(national) {
  let n = String(national || '').replace(/\D/g, '');
  if (n.startsWith('0') && n.length > 1) n = n.slice(1);
  return n;
}

/**
 * @param {string} dial e.g. +82
 * @param {string} national local digits
 */
export function toE164Phone(dial, national) {
  const code = String(dial || '').replace(/\D/g, '');
  const num = sanitizeNationalDigits(national);
  if (!code || !num) return '';
  return `+${code}${num}`;
}

/**
 * @param {string | null | undefined} raw stored or pasted value
 * @param {string} [fallbackDial]
 * @returns {{ dial: string, national: string, e164: string }}
 */
export function parseStoredPhone(raw, fallbackDial = '+84') {
  const text = String(raw || '').trim();
  const dialDefault = PHONE_DIAL_CODES.some((r) => r.dial === fallbackDial) ? fallbackDial : '+84';

  if (!text) {
    return { dial: dialDefault, national: '', e164: '' };
  }

  const allDigits = text.replace(/\D/g, '');
  if (!allDigits) {
    return { dial: dialDefault, national: '', e164: '' };
  }

  if (text.startsWith('+') || allDigits.length > 10) {
    for (const code of DIAL_DIGITS_DESC) {
      if (allDigits.startsWith(code) && allDigits.length > code.length) {
        const national = allDigits.slice(code.length);
        const dial = `+${code}`;
        return { dial, national, e164: `+${code}${national}` };
      }
    }
  }

  const national = sanitizeNationalDigits(allDigits);
  const e164 = toE164Phone(dialDefault, national);
  return { dial: dialDefault, national, e164 };
}

/**
 * Normalize for DB storage (E.164 or empty).
 * @param {string | null | undefined} raw
 * @param {string} [fallbackDial]
 */
export function normalizeStoredPhone(raw, fallbackDial) {
  const { e164 } = parseStoredPhone(raw, fallbackDial);
  return e164 || '';
}

/**
 * @param {string | null | undefined} e164
 */
export function isValidE164Phone(e164) {
  const s = String(e164 || '').trim();
  return /^\+\d{8,15}$/.test(s);
}
